import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Printer, ArrowLeft, FileSignature, Save, Download, Send } from "lucide-react";
import axios from "axios";
import { FIRM } from "@/lib/firm";
import { sha256 } from "@/lib/sha256";
import { numberToWords } from "@/lib/format";
import { dash, money, fmtDate, borrowerSigOf } from "@/pages/CollateralMotion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useQuery = () => new URLSearchParams(useLocation().search);
const todayISO = () => new Date().toISOString().slice(0, 10);

export const MR_FORMS = [
  { key: "agreement", label: "Full Agreement (Articles I–XXIX + Schedules A–G + Coupon)", short: "Full Agreement" },
  { key: "coupon", label: "Detachable Substitute Payment Coupon only", short: "Coupon" },
];

const CursiveSig = ({ name }) => name
  ? <span className="mr-sig-cursive" style={{ fontSize: "20pt", lineHeight: 1 }}>{name.startsWith("/s/") ? name : `/s/ ${name}`}</span>
  : <span>______________________________</span>;

export const MR_CSS = `
  .mr-sheet{background:#fff;color:#14140F;max-width:8.5in;margin:0 auto 28px;padding:0.55in 0.6in;box-shadow:0 10px 40px rgba(0,0,0,.4);font-family:'Times New Roman',Georgia,serif;font-size:10.5pt;line-height:1.4;}
  .mr-head{text-align:center;border-bottom:2.5px solid #5A2E12;padding-bottom:9px;margin-bottom:12px;}
  .mr-eyebrow{font-size:8pt;letter-spacing:.16em;color:#5A2E12;font-weight:700;text-transform:uppercase;}
  .mr-title{font-size:16pt;font-weight:700;margin:5px 0 3px;text-transform:uppercase;letter-spacing:.02em;}
  .mr-sub{font-size:8.5pt;color:#555;font-style:italic;}
  .mr-sig-cursive{font-family:'Great Vibes','Snell Roundhand',cursive;color:#1b2a52;}
  .mr-art{margin:12px 0 2px;font-size:11pt;font-weight:700;color:#5A2E12;text-transform:uppercase;letter-spacing:.03em;border-bottom:1px solid #d8cfc4;padding-bottom:2px;page-break-after:avoid;}
  .mr-sheet p{text-align:justify;margin:5px 0;}
  .mr-f{display:flex;gap:8px;padding:2px 6px;border:1px solid #ddd6cc;border-top:none;}
  .mr-f:first-of-type{border-top:1px solid #ddd6cc;}
  .mr-fl{flex:0 0 40%;font-weight:600;color:#333;font-size:9pt;text-transform:uppercase;letter-spacing:.02em;}
  .mr-fv{flex:1;font-size:9.5pt;}
  .mr-blank{display:inline-block;min-width:150px;border-bottom:1px solid #333;}
  .mr-note{font-size:8pt;color:#6b6b63;margin-top:4px;font-style:italic;}
  .mr-grid2{display:flex;gap:14px;} .mr-grid2>div{flex:1;}
  .mr-check-chk{font-weight:700;}
  .mr-schedule{margin-top:10px;padding-top:6px;border-top:2px dashed #b9b1a4;}
  .mr-sig-block{margin:8px 0;} .mr-sig-block p{margin:3px 0;font-size:9.5pt;}
  .mr-sigrow{display:flex;gap:22px;margin-top:8px;} .mr-sigrow>div{flex:1;}
  .mr-hash{font-family:'Courier New',monospace;font-size:8.5pt;word-break:break-all;color:#14204a;background:#f3efe9;padding:1px 4px;border-radius:3px;}

  /* ---- Draft-check styled Detachable Substitute Payment Coupon ---- */
  .mr-coupon-wrap{margin:14px 0;page-break-inside:avoid;}
  .mr-coupon-cut{text-align:center;font-size:7.5pt;letter-spacing:.3em;color:#8a8a82;margin:6px 0 4px;}
  .mr-check{position:relative;border:2px solid #1b4d3e;border-radius:7px;padding:14px 16px 12px;background:
      repeating-linear-gradient(45deg,#f6fbf8,#f6fbf8 7px,#eef6f1 7px,#eef6f1 14px);
      box-shadow:inset 0 0 0 1px #2e6f5e, 0 2px 6px rgba(0,0,0,.12);overflow:hidden;}
  .mr-check::after{content:"SUBSTITUTE PAYMENT COUPON · NON-NEGOTIABLE";position:absolute;top:50%;left:50%;
      transform:translate(-50%,-50%) rotate(-18deg);font-size:22pt;font-weight:800;color:rgba(27,77,62,.07);
      letter-spacing:.08em;white-space:nowrap;pointer-events:none;}
  .mr-check-top{display:flex;justify-content:space-between;align-items:flex-start;position:relative;}
  .mr-check-drawer{font-size:9.5pt;line-height:1.25;}
  .mr-check-drawer .nm{font-weight:800;font-size:11pt;color:#14321f;text-transform:uppercase;letter-spacing:.03em;}
  .mr-check-no{text-align:right;font-size:9pt;color:#14321f;}
  .mr-check-no .big{font-size:13pt;font-weight:800;}
  .mr-check-payline{display:flex;align-items:flex-end;gap:10px;margin:14px 0 4px;position:relative;}
  .mr-check-paylabel{font-size:7.5pt;text-transform:uppercase;letter-spacing:.08em;color:#2e6f5e;font-weight:700;white-space:nowrap;padding-bottom:3px;}
  .mr-check-payee{flex:1;border-bottom:1.5px solid #1b4d3e;font-size:12pt;font-weight:700;color:#14321f;padding:0 4px 2px;}
  .mr-check-amtbox{border:2px solid #1b4d3e;border-radius:4px;background:#fff;padding:3px 10px;font-size:13pt;font-weight:800;color:#14321f;white-space:nowrap;}
  .mr-check-words{display:flex;align-items:flex-end;gap:8px;margin:2px 0 10px;position:relative;}
  .mr-check-wordline{flex:1;border-bottom:1px solid #1b4d3e;font-size:10pt;font-style:italic;color:#14321f;padding:0 4px 2px;text-transform:capitalize;}
  .mr-check-wordsuffix{font-size:8pt;color:#2e6f5e;padding-bottom:2px;}
  .mr-check-mid{display:flex;justify-content:space-between;gap:16px;position:relative;font-size:8.5pt;color:#1f3a2c;margin-top:4px;}
  .mr-check-memo{flex:1;}
  .mr-check-memo .ln{border-bottom:1px solid #6f8f80;min-width:150px;display:inline-block;padding:0 3px;font-weight:600;}
  .mr-check-sig{flex:0 0 42%;text-align:center;}
  .mr-check-sig .sigln{border-top:1px solid #1b4d3e;margin-top:20px;padding-top:2px;font-size:7.5pt;text-transform:uppercase;letter-spacing:.06em;color:#2e6f5e;}
  .mr-micr{margin-top:10px;padding-top:7px;border-top:1px dashed #6f8f80;font-family:'Courier New',monospace;font-size:12pt;letter-spacing:.14em;color:#14321f;text-align:center;position:relative;}
  .mr-coupon-detail{border:1px solid #c9d8cf;border-radius:5px;margin-top:8px;font-size:8.3pt;}
  .mr-coupon-detail .row{display:flex;border-top:1px solid #dfe8e2;}
  .mr-coupon-detail .row:first-child{border-top:none;}
  .mr-coupon-detail .c{flex:1;padding:3px 7px;}
  .mr-coupon-detail .c .lb{font-size:6.6pt;text-transform:uppercase;letter-spacing:.07em;color:#5f7a6d;font-weight:700;}
  .mr-coupon-detail .c .vl{font-size:8.8pt;color:#14321f;font-weight:600;}
  .mr-coupon-box{border:1px solid #c9d8cf;min-height:16px;padding:3px 6px;margin:3px 0;font-size:8.2pt;}

  @media print{
    body{background:#fff!important;}
    .mr-workbench{background:#fff!important;min-height:0!important;}
    .no-print{display:none!important;}
    .cm-print-area{padding:0!important;max-width:none!important;}
    .mr-sheet{box-shadow:none!important;margin:0!important;max-width:none!important;padding:0.3in 0.4in!important;}
    .mr-art,.mr-title{break-after:avoid;}
    @page{size:letter;margin:0.35in;}
  }
`;

const B = () => <span className="mr-blank" />;
const F = ({ label, children }) => (
  <div className="mr-f"><span className="mr-fl">{label}</span><span className="mr-fv">{children || <B />}</span></div>
);
const val = (shared, key, auto) => (shared[key] !== undefined && shared[key] !== "" ? shared[key] : (auto || ""));

export const mrDefaults = () => ({
  date: todayISO(), agreementNo: "", replacementNoteNo: "", couponNo: "001",
  payee: FIRM.name, maker: "", faceAmount: "", interestRate: "", termMonths: "360",
  firstPaymentDate: "", maturityDate: "", designation: "payment",
  governingState: FIRM.notaryState, arbitrationProvider: "American Arbitration Association (AAA)",
  serviceDescription: "Financial structuring, note replacement and collateral administration services rendered by SEUTRADE FINANCIAL SERVICES.",
});

const DESIGNATIONS = {
  payment: "Payment against Replacement Note",
  service: "Service payment",
  barter: "Barter / exchange consideration",
  settlement: "Contractual settlement",
  assignment: "Assignment of Payment Right",
  secured: "Application to secured obligation",
};

function amountWords(n) {
  const v = Math.round(Number(n) || 0);
  if (!v) return "";
  try { return numberToWords(v); } catch { return ""; }
}

export function MortgageReplacementDoc({ app, shared, last, formKey }) {
  const binder = app.binder || {};
  const borrowerName = app.borrower_full_name || app.borrower_name || "";
  const borrowerAddr = app.borrower_address || "";
  const lenderName = app.current_lender || "";
  const lenderAddr = app.current_lender_address || binder.lenderAddress || "";
  const propAuto = app.property_or_collateral || app.property || app.property_address || binder.collateral || "";
  const acctAuto = app.loan_account_no || app.account_no || app.loan_number || binder.accountNo || app.account_number || "";
  const balAuto = app.payoff_amount || app.current_balance || binder.payoffAmount || "";
  const auto = {
    maker: borrowerName,
    faceAmount: balAuto,
    interestRate: app.current_rate || binder.newRate || "",
    legalDesc: app.legal_description || binder.legalDescription || propAuto,
  };
  const g = (k) => val(shared, k, auto[k]);

  const ref6 = String(parseInt(sha256(String(app.id || borrowerName || "seed")).slice(0, 8), 16)).slice(0, 6).padEnd(6, "0");
  const yr = (g("date") || todayISO()).slice(0, 4);
  const agreementNo = g("agreementNo") || `STF-IMRA-${yr}-${ref6}`;
  const noteNo = g("replacementNoteNo") || `STF-RN-${yr}-${ref6}`;
  const account = acctAuto || `STF-LN-${yr}-${ref6}`;
  const face = g("faceAmount");
  const rate = g("interestRate");
  const term = g("termMonths");
  const bSig = borrowerSigOf(app);
  const firmSig = `/s/ ${FIRM.officer}`;
  const stampTime = `${(g("date") || todayISO())} · ${new Date().toISOString().slice(11, 19)} UTC`;
  const execHash = sha256([FIRM.name, agreementNo, noteNo, borrowerName, lenderName, face, account, g("date")].map((x) => String(x || "")).join("|"));
  const designation = DESIGNATIONS[shared.designation] || DESIGNATIONS.payment;
  const routing = "0" + String(parseInt(ref6, 10) % 100000000).padStart(8, "0");

  const CheckCoupon = () => (
    <div className="mr-coupon-wrap" data-testid={`mr-coupon-${app.id}`}>
      <div className="mr-coupon-cut">— — — — — — — — — — —  DETACH HERE  — — — — — — — — — — —</div>
      <div className="mr-check">
        <div className="mr-check-top">
          <div className="mr-check-drawer">
            <div className="nm">{FIRM.name}</div>
            <div>{FIRM.address}</div>
            <div>EIN {FIRM.ein} · Issuer / Payor of Substitute Payment Coupon</div>
          </div>
          <div className="mr-check-no">
            <div>Coupon No. <span className="big">{g("couponNo") || "001"}</span></div>
            <div>Issue Date: {g("date") ? fmtDate(g("date")) : "____________"}</div>
            <div>Payment Date: {g("maturityDate") ? fmtDate(g("maturityDate")) : (g("date") ? fmtDate(g("date")) : "____________")}</div>
          </div>
        </div>
        <div className="mr-check-payline">
          <span className="mr-check-paylabel">Pay to the<br />Order of</span>
          <span className="mr-check-payee">{dash(lenderName || g("payee"))}</span>
          <span className="mr-check-amtbox">{face ? money(face) : "$ ____________"}</span>
        </div>
        <div className="mr-check-words">
          <span className="mr-check-wordline">{amountWords(face) || "____________________________________________"}</span>
          <span className="mr-check-wordsuffix">— as lawful contractual consideration</span>
        </div>
        <div className="mr-check-mid">
          <div className="mr-check-memo">
            Memo: <span className="ln">{designation}</span><br />
            Replacement Note No.: <span className="ln">{noteNo}</span><br />
            Account / Loan No.: <span className="ln">{dash(account)}</span>
          </div>
          <div className="mr-check-sig">
            <CursiveSig name={firmSig} />
            <div className="sigln">Authorized Signature — {FIRM.officer}, Authorized Officer</div>
          </div>
        </div>
        <div className="mr-micr">⑆ {routing} ⑆ {String(account).replace(/[^0-9A-Za-z]/g, "").slice(-10) || "0000000000"} ⑈ {g("couponNo") || "001"} ⑈</div>
      </div>

      <div className="mr-coupon-detail">
        <div className="row">
          <div className="c"><div className="lb">Maker</div><div className="vl">{dash(borrowerName)}</div></div>
          <div className="c"><div className="lb">Payee</div><div className="vl">{dash(lenderName || g("payee"))}</div></div>
          <div className="c"><div className="lb">Face Amount</div><div className="vl">{face ? money(face) : "—"}</div></div>
        </div>
        <div className="row">
          <div className="c"><div className="lb">Property</div><div className="vl">{dash(propAuto)}</div></div>
          <div className="c"><div className="lb">Proposed Credit</div><div className="vl">{face ? money(face) : "—"}</div></div>
          <div className="c"><div className="lb">Remaining Balance After Credit</div><div className="vl">$0.00</div></div>
        </div>
        <div className="row"><div className="c" style={{ flex: "none", width: "100%" }}>
          <div className="lb">Payment / Settlement Designation</div>
          <div className="vl">{designation} — tendered in full satisfaction of the referenced Replacement Note and secured obligation.</div>
        </div></div>
      </div>

      <div style={{ fontSize: "8.3pt", marginTop: 6 }}>
        <div className="mr-art" style={{ marginTop: 6 }}>Coupon Holder / Payment Record</div>
        <div className="mr-coupon-detail">
          <div className="row">
            <div className="c"><div className="lb">Holder</div><div className="vl">&nbsp;</div></div>
            <div className="c"><div className="lb">Date Received</div><div className="vl">&nbsp;</div></div>
            <div className="c"><div className="lb">Amount Received</div><div className="vl">&nbsp;</div></div>
          </div>
          <div className="row">
            <div className="c"><div className="lb">Payment Method</div><div className="vl">&nbsp;</div></div>
            <div className="c"><div className="lb">Applied To</div><div className="vl">&nbsp;</div></div>
            <div className="c"><div className="lb">Processed By</div><div className="vl">&nbsp;</div></div>
          </div>
        </div>
        <p style={{ margin: "5px 0 2px" }}><strong>Counterparty Acceptance:</strong> [ ] Accepted as payment &nbsp; [ ] Accepted as barter/exchange consideration &nbsp; [ ] Accepted for review &nbsp; [ ] Accepted subject to verification &nbsp; [ ] Rejected</p>
        <div className="mr-coupon-detail">
          <div className="row">
            <div className="c"><div className="lb">Amount Recognized / Credited ($)</div><div className="vl">&nbsp;</div></div>
            <div className="c"><div className="lb">Credit Date</div><div className="vl">&nbsp;</div></div>
            <div className="c"><div className="lb">Remaining Balance ($)</div><div className="vl">&nbsp;</div></div>
          </div>
        </div>
        <p className="mr-note">This Detachable Substitute Payment Coupon is a private contractual instrument issued under Article XVIII of the Integrated Mortgage Replacement Agreement. It is non-negotiable, is not a check or draft drawn on any bank, and is not an obligation of, or guaranteed by, the United States or any agency thereof. Its effect depends on the counterparty's acceptance and the substance of the underlying transaction.</p>
      </div>
    </div>
  );

  if (formKey === "coupon") {
    return (
      <div className="mr-sheet" data-testid={`mr-solo-coupon-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
        <div className="mr-head">
          <div className="mr-eyebrow">SEUTRADE FINANCIAL SERVICES · Integrated Mortgage Replacement Agreement</div>
          <div className="mr-title" style={{ fontSize: "14pt" }}>Detachable Substitute Payment Coupon</div>
          <div className="mr-sub">Article XVIII · Schedule G — issued against Replacement Note No. {noteNo}</div>
        </div>
        <F label="Agreement No.">{agreementNo}</F>
        <F label="Replacement Note No.">{noteNo}</F>
        <F label="Maker / Payee">{[dash(borrowerName), dash(lenderName || g("payee"))].join("  →  ")}</F>
        <CheckCoupon />
      </div>
    );
  }

  const Art = ({ n, t }) => <div className="mr-art">Article {n} — {t}</div>;

  return (
    <div className="mr-sheet" data-testid={`mr-doc-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
      <div className="mr-head">
        <div className="mr-eyebrow">Private Commercial Agreement · Not a Government Instrument</div>
        <div className="mr-title">Integrated Mortgage Replacement Agreement</div>
        <div className="mr-sub">Mortgage Replacement · Service / Barter / Arbitration · Assignment / Collateral — with Detachable Substitute Payment Coupon</div>
      </div>

      <F label="Agreement No. / Date">{agreementNo} · {g("date") ? fmtDate(g("date")) : <B />}</F>
      <F label="Borrower / Maker / Grantor">{dash(borrowerName)}</F>
      <F label="Borrower Address">{dash(borrowerAddr)}</F>
      <F label="Lender / Payee / Secured Party">{dash(lenderName || g("payee"))}</F>
      <F label="Lender Address">{dash(lenderAddr)}</F>
      <F label="Service Provider / Note Replacement Party">{FIRM.name} · {FIRM.address}</F>
      <F label="Property / Collateral">{dash(propAuto)}</F>
      <F label="Loan / Account No.">{dash(account)}</F>

      <Art n="I" t="Purpose, Consideration and Integrated Transaction" />
      <p>This Integrated Mortgage Replacement Agreement (the "Agreement") integrates three coordinated pathways into a single contract: (1) the <strong>Mortgage Replacement Pathway</strong> (replacement of the Existing Note and Existing Security Instrument with a Replacement Note and Replacement Security Instrument); (2) the <strong>Service / Barter / Arbitration Pathway</strong> (lawful consideration through services rendered, barter/exchange, and contractual arbitration of any Payment Right); and (3) the <strong>Assignment / Collateral Pathway</strong> (assignment of Payment Rights and grant of a security interest in Collateral). Each pathway is independent yet mutually reinforcing, and collectively constitutes the consideration exchanged among the parties.</p>

      <Art n="II" t="Definitions and Construction" />
      <p>"Existing Note" and "Existing Security Instrument" mean the current obligation and lien identified in Schedule A/B. "Replacement Note" and "Replacement Security Instrument" mean the instruments described in Articles IV–V and Schedule D. "Service Claim," "Arbitration Award," "Payment Right," and "Collateral" have the meanings given in the respective Articles below. Headings are for convenience only.</p>

      <Art n="III" t="Existing Indebtedness and Existing Security" />
      <F label="Original Creditor">{dash(lenderName)}</F>
      <F label="Debtor / Borrower">{dash(borrowerName)}</F>
      <F label="Existing Note (Original Principal / Current Balance)">{dash(propAuto ? "" : "")}{face ? money(face) : <B />}</F>
      <F label="Existing Security Instrument (Property)">{dash(propAuto)}</F>
      <F label="Loan / Account No.">{dash(account)}</F>

      <Art n="IV" t="Complete Replacement Promissory Note" />
      <F label="Replacement Note No.">{noteNo}</F>
      <F label="Maker">{dash(borrowerName)}</F>
      <F label="Payee / Holder">{dash(lenderName || g("payee"))}</F>
      <F label="Principal Sum">{face ? money(face) : <B />}</F>
      <F label="Interest Rate">{rate ? `${rate}%` : <B />}</F>
      <F label="Term (months) / First Payment / Maturity">{[term, g("firstPaymentDate") ? fmtDate(g("firstPaymentDate")) : "____", g("maturityDate") ? fmtDate(g("maturityDate")) : "____"].join(" · ")}</F>
      <p>For value received, the Maker promises to pay to the order of the Payee the Principal Sum together with interest as set forth above, in accordance with the payment schedule, subject to the default, acceleration and prepayment provisions customarily applicable to such notes.</p>

      <Art n="V" t="Complete Replacement Security Deed / Mortgage / Deed of Trust" />
      <F label="Grantor">{dash(borrowerName)}</F>
      <F label="Secured Party">{dash(lenderName || g("payee"))}</F>
      <F label="Property">{dash(propAuto)}</F>
      <F label="Legal Description">{dash(g("legalDesc"))}</F>
      <F label="Maximum Secured Amount">{face ? money(face) : <B />}</F>
      <p>The Grantor grants a security interest in / conveys the Property to secure the Replacement Note, subject to the taxes, insurance and maintenance covenants, and the power-of-sale / foreclosure remedies permitted under the governing jurisdiction.</p>

      <Art n="VI" t="Complete Disposition of Existing Note and Security Instrument" />
      <p>Upon execution and performance, the Existing Note and Existing Security Instrument shall be [ ] satisfied · [ ] released · [ ] modified · [ ] assigned · [ ] cancelled · [ ] reconveyed · [X] replaced by the Replacement Note and Replacement Security Instrument, such disposition to be recorded as required by Article XX.</p>

      <Art n="VII" t="Consideration and Exchange" />
      <p>Consideration may consist of money, services, property, barter, exchange, contractual payment rights, settlement, and/or assignments, in any lawful combination, the sufficiency of which is acknowledged.</p>

      <Art n="VIII" t="Complete Service Agreement" />
      <F label="Service Provider">{FIRM.name}</F>
      <F label="Service Recipient">{dash(borrowerName)}</F>
      <F label="Description of Services">{g("serviceDescription")}</F>
      <F label="Agreed Value">{face ? money(face) : <B />}</F>

      <Art n="IX" t="Complete Barter and Exchange Agreement" />
      <p>The parties may exchange services, property, or other lawful consideration of equivalent agreed value in lieu of monetary payment, with any such exchange documented in Schedule E.</p>

      <Art n="X" t="Service Payment Obligation" />
      <p>Upon maturation of the service payment obligation, payment, barter, or exchange becomes due in accordance with the agreed value and terms, enforceable as a Payment Right.</p>

      <Art n="XI" t="Complete Contractual Arbitration Agreement" />
      <F label="Arbitration Provider / Rules">{g("arbitrationProvider")}</F>
      <p>Disputes arising from the Service Agreement or any Payment Right shall be resolved by binding arbitration under the stated provider's rules, including expedited procedures where applicable; an Arbitration Award may be identified as a Payment Right or asset under Article XIV.</p>

      <Art n="XII" t="Self-Executing Contractual Payment Mechanism" />
      <p>Payment obligations may become operative automatically upon a defined triggering event, without further action, to the extent permitted by applicable contract law.</p>

      <Art n="XIII" t="Chosen Action / Election of Contractual Remedy" />
      <p>A holder of a Payment Right may elect among payment, barter, exchange, settlement, assignment, arbitration, or application to the secured obligation.</p>

      <Art n="XIV" t="Award / Payment Right Asset Schedule" />
      <p>An Arbitration Award may be scheduled as a Payment Right/asset, with award details recorded in Schedule E.</p>

      <Art n="XV" t="Complete Assignment Agreement" />
      <F label="Assignor">{dash(borrowerName)}</F>
      <F label="Assignee">{FIRM.name}</F>
      <F label="Assigned Amount / Original Amount">{face ? money(face) : <B />}</F>

      <Art n="XVI" t="Complete Security Agreement" />
      <F label="Maximum Amount">{face ? money(face) : <B />}</F>
      <p>The debtor grants a security interest in the Collateral identified in Schedule F; attachment, perfection, priority, default and remedies are governed by applicable secured-transactions law.</p>

      <Art n="XVII" t="Complete Collateral Assignment and Security Interest Agreement" />
      <p>The Payment Right and any Award are collaterally assigned and a security interest therein is granted, with further-assurances covenants.</p>

      <Art n="XVIII" t="Complete Detachable Substitute Payment Coupon Agreement" />
      <p>Attached to the Replacement Note is the Detachable Substitute Payment Coupon (Coupon No. {g("couponNo") || "001"}) set forth below and in Schedule G. The Coupon functions as a private contractual payment/settlement instrument designating the applicable consideration and proposed credit; it is non-negotiable and is not a bank check or draft.</p>
      <CheckCoupon />

      <Art n="XIX" t="Accounting, Records and Tax Reporting" />
      <p>Each party shall maintain records of the transaction. Tax treatment depends on the substance of the transaction; no party makes any representation as to another's tax position.</p>

      <Art n="XX" t="Recording and Perfection" />
      <p>The parties shall record and perfect the Replacement Security Instrument (real property) and file financing statements (personal property) as required to establish priority.</p>

      <Art n="XXI" t="Accord and Satisfaction" />
      <p>This Agreement may operate as an accord and satisfaction of the Existing Indebtedness under applicable law upon full performance of the elected pathway.</p>

      <Art n="XXII" t="No Governmental Guarantee" />
      <p>No instrument, coupon, note, award, or obligation created under this Agreement is issued, insured, or guaranteed by the United States, the Treasury, the Internal Revenue Service, or any agency or instrumentality thereof.</p>

      <Art n="XXIII" t="Notices" />
      <p>Notices shall be delivered to the addresses stated above or as later designated in writing, by hand, courier, certified mail, or electronic fax.</p>

      <Art n="XXIV" t="Governing Law" />
      <F label="Governing Jurisdiction">{g("governingState")}</F>
      <p>Applicable contract and commercial law, secured-transactions law, and the property-jurisdiction law of the above state govern this Agreement.</p>

      <Art n="XXV" t="Severability" />
      <p>If any provision is unenforceable, the remaining provisions remain in full force and effect.</p>

      <Art n="XXVI" t="Entire Agreement and Incorporation" />
      <p>Articles I–XXIX and Schedules A–G constitute the entire, integrated agreement and are incorporated by reference.</p>

      <Art n="XXVII" t="Order of Operation" />
      <p>The Mortgage Pathway, Service Pathway, and Collateral Pathway are intended to operate in the sequence described herein, culminating in satisfaction of the Existing Indebtedness and tender of the Detachable Substitute Payment Coupon.</p>

      <Art n="XXVIII" t="Execution and Acknowledgment" />
      <div className="mr-sigrow">
        <div className="mr-sig-block">
          <p style={{ fontWeight: 700 }}>Borrower / Maker / Grantor</p>
          <p>Signature: <CursiveSig name={bSig} /></p>
          <p>Name: {dash(borrowerName)}</p>
          <p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
        <div className="mr-sig-block">
          <p style={{ fontWeight: 700 }}>Lender / Payee / Secured Party · Service Provider</p>
          <p>Signature: <CursiveSig name={firmSig} /> — {FIRM.officer}, Authorized Officer</p>
          <p>For: {FIRM.name}</p>
          <p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
      </div>
      <p>Execution Date / Time: {stampTime} · Execution Hash (SHA-256):</p>
      <p><span className="mr-hash">{execHash}</span></p>
      <p style={{ fontSize: "9pt" }}>Subscribed and acknowledged before me this ______ day of __________, 20____ — Notary Public, State of {dash(g("governingState"))}. [Notary Seal]</p>

      <Art n="XXIX" t="Final Provisions" />
      <p>This Agreement binds the parties and their successors and assigns and may be executed in counterparts, including electronically.</p>

      {/* Schedules A–G */}
      <div className="mr-schedule">
        <div className="mr-art" style={{ marginTop: 4 }}>Schedule A — Existing Note</div>
        <F label="Original Principal / Current Balance">{face ? money(face) : <B />}</F>
        <F label="Original Creditor / Loan No.">{[dash(lenderName), dash(account)].join(" · ")}</F>
      </div>
      <div className="mr-schedule">
        <div className="mr-art" style={{ marginTop: 4 }}>Schedule B — Existing Security Instrument</div>
        <F label="Instrument / Property">{dash(propAuto)}</F>
      </div>
      <div className="mr-schedule">
        <div className="mr-art" style={{ marginTop: 4 }}>Schedule C — Property Legal Description</div>
        <F label="Legal Description">{dash(g("legalDesc"))}</F>
      </div>
      <div className="mr-schedule">
        <div className="mr-art" style={{ marginTop: 4 }}>Schedule D — Replacement Note Summary</div>
        <div className="mr-grid2">
          <div><F label="Note No.">{noteNo}</F><F label="Principal">{face ? money(face) : <B />}</F></div>
          <div><F label="Rate / Term">{[rate ? `${rate}%` : "____", `${term} mo`].join(" · ")}</F><F label="Maturity">{g("maturityDate") ? fmtDate(g("maturityDate")) : <B />}</F></div>
        </div>
      </div>
      <div className="mr-schedule">
        <div className="mr-art" style={{ marginTop: 4 }}>Schedule E — Service Claim / Arbitration Award</div>
        <F label="Agreed Value / Award Amount">{face ? money(face) : <B />}</F>
        <F label="Provider">{g("arbitrationProvider")}</F>
      </div>
      <div className="mr-schedule">
        <div className="mr-art" style={{ marginTop: 4 }}>Schedule F — Collateral Schedule</div>
        <F label="Collateral">{dash(propAuto)} · the Replacement Note, the Payment Right, and any Arbitration Award</F>
      </div>
      <div className="mr-schedule">
        <div className="mr-art" style={{ marginTop: 4 }}>Schedule G — Detachable Payment Coupon</div>
        <CheckCoupon />
      </div>
    </div>
  );
}

const Ctl = ({ label, value, onChange, type = "text", options }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    {options
      ? <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#5A2E12]">
          {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      : <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#5A2E12]" />}
  </div>
);

export default function MortgageReplacement() {
  const { appId } = useParams();
  const q = useQuery();
  const idsParam = q.get("ids");
  const [formSel, setFormSel] = useState(() => q.get("form") || "");
  const activeForm = formSel || null;
  const activeLabel = (MR_FORMS.find((f) => f.key === formSel) || {}).label || "";
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [shared, setShared] = useState(mrDefaults);
  const set = (k) => (v) => setShared((p) => ({ ...p, [k]: v }));

  const token = localStorage.getItem("th_admin_token");
  const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  useEffect(() => {
    const ids = idsParam ? idsParam.split(",").map((x) => x.trim()).filter(Boolean) : (appId ? [appId] : []);
    (async () => {
      const results = [];
      for (const id of ids) {
        try { const r = await axios.get(`${API}/borrower/applications/${id}`, cfg); if (r.data.application) results.push(r.data.application); } catch { /* skip */ }
      }
      if (results[0]) {
        const savedSettings = (results.find((a) => a.mortgage_replacement_settings) || {}).mortgage_replacement_settings || null;
        if (savedSettings) setShared((p) => ({ ...p, ...savedSettings }));
      }
      setApps(results);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [appId, idsParam]);

  const saveDocData = async () => {
    if (!apps.length) return;
    setSaving(true); setSavedMsg("");
    try {
      await Promise.all(apps.map((a) => axios.post(`${API}/admin/borrower-applications/${a.id}/motion-data`,
        { mortgage_replacement_settings: shared }, cfg)));
      setSavedMsg("Saved to file ✓"); setTimeout(() => setSavedMsg(""), 4000);
    } catch { setSavedMsg("Save failed"); setTimeout(() => setSavedMsg(""), 4000); }
    finally { setSaving(false); }
  };

  const [dlBusy, setDlBusy] = useState(false);
  const downloadPdf = async () => {
    if (!apps.length) return;
    setDlBusy(true);
    try {
      const r = await axios.post(`${API}/admin/mortgage-replacement/bulk-pdf`, { app_ids: apps.map((a) => a.id), form: activeForm }, { ...cfg, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const base = activeForm === "coupon" ? "mortgage_replacement_coupon" : "integrated_mortgage_replacement_agreement";
      a.href = url; a.download = apps.length > 1 ? `${base}_${apps.length}.pdf` : `${base}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || "Could not build the merged PDF."); }
    finally { setDlBusy(false); }
  };

  const [faxOpen, setFaxOpen] = useState(false);
  const [faxTo, setFaxTo] = useState("");
  const [faxBusy, setFaxBusy] = useState(false);
  const [faxMsg, setFaxMsg] = useState(null);
  const sendFax = async () => {
    if (!apps.length || !faxTo.trim()) return;
    setFaxBusy(true); setFaxMsg(null);
    try {
      const r = await axios.post(`${API}/admin/mortgage-replacement/efax`, { app_ids: apps.map((a) => a.id), to: faxTo.trim(), form: activeForm, label: `${activeForm ? activeLabel : "Integrated Mortgage Replacement Agreement"} · ${apps.length} borrower(s)` }, cfg);
      const fid = r.data?.fax?.fax_id || r.data?.fax?.id || "";
      const st = r.data?.fax?.status || "queued";
      setFaxMsg({ ok: true, text: `✓ Fax submitted to ${faxTo.trim()} — ${apps.length} doc(s). Telnyx ID: ${fid || "n/a"} · Status: ${st}` });
    } catch (e) { setFaxMsg({ ok: false, text: e.response?.data?.detail || "Fax failed to send." }); }
    finally { setFaxBusy(false); }
  };

  if (loading) return <div style={{ padding: 40, color: "#ECECE4", fontFamily: "serif" }}>Loading…</div>;

  return (
    <div className="mr-workbench" style={{ background: "#0B0B07", minHeight: "100vh" }}>
      <div className="no-print" style={{ background: "#1A0E06", borderBottom: "1px solid #5A2E12", padding: "12px 18px" }}>
        <div style={{ maxWidth: "8.5in", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <a href="/admin" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#D8A77A", fontSize: 12 }}><ArrowLeft size={14} /> Back</a>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#D8A77A", fontWeight: 700, letterSpacing: ".06em", fontSize: 12 }}>
            <FileSignature size={16} /> {activeForm ? (MR_FORMS.find((f) => f.key === formSel) || {}).short?.toUpperCase() : "MORTGAGE REPLACEMENT AGMT"}{apps.length > 1 ? ` · ${apps.length} BORROWERS` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select value={formSel} onChange={(e) => setFormSel(e.target.value)} data-testid="mr-form-select"
              style={{ background: "#0F0F0A", color: "#ECECE4", border: "1px solid #5A2E12", borderRadius: 6, padding: "9px 10px", fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer", maxWidth: 260 }}>
              {MR_FORMS.map((f) => <option key={f.key} value={f.key === "agreement" ? "" : f.key}>{f.label}</option>)}
            </select>
            <button onClick={saveDocData} disabled={saving || !apps.length} data-testid="mr-save" style={btn("#D8A77A", "#5A2E12")}><Save size={13} /> {saving ? "Saving…" : "Save to File"}</button>
            <button onClick={downloadPdf} disabled={dlBusy || !apps.length} data-testid="mr-download" style={btn("#D8A77A", "#5A2E12")}><Download size={13} /> {dlBusy ? "Building…" : "Download PDF"}</button>
            <button onClick={() => setFaxOpen((x) => !x)} disabled={!apps.length} data-testid="mr-efax-toggle" style={btn("#5FBFA0", "#2E6F5E")}><Send size={13} /> eFax</button>
            <button onClick={() => window.print()} data-testid="mr-print" style={{ ...btn("#0B0B07", "#B8860B"), background: "#E4C97A", color: "#0B0B07" }}><Printer size={13} /> Print / Save as PDF</button>
          </div>
        </div>
        {savedMsg && <div className="text-[12px] text-[#5FE0B0] mt-2" data-testid="mr-saved-msg" style={{ maxWidth: "8.5in", margin: "6px auto 0" }}>{savedMsg}</div>}

        {faxOpen && (
          <div style={{ maxWidth: "8.5in", margin: "10px auto 0" }} className="rounded-md border border-[#2E6F5E]/40 bg-[#0F1410] p-3" data-testid="mr-efax-panel">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#5FBFA0] mb-2 flex items-center gap-2"><Send size={12} /> eFax {activeForm ? activeLabel : "Mortgage Replacement Agreement"}{apps.length > 1 ? ` (all ${apps.length} merged)` : ""}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Destination fax number</label>
                <input value={faxTo} onChange={(e) => setFaxTo(e.target.value)} data-testid="mr-efax-to" placeholder="+1 ..."
                  className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#2E6F5E]" />
              </div>
              <button onClick={sendFax} disabled={faxBusy || !faxTo.trim()} data-testid="mr-efax-send" style={{ ...btn("#0B0B07", "#2E6F5E"), background: "#5FBFA0", color: "#0B0B07" }}><Send size={13} /> {faxBusy ? "Sending…" : "Send eFax"}</button>
            </div>
            {faxMsg && <div data-testid="mr-efax-msg" className={`text-[12px] font-bold mt-2 ${faxMsg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`}>{faxMsg.text}</div>}
          </div>
        )}

        <div style={{ maxWidth: "8.5in", margin: "12px auto 0" }} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Ctl label="Agreement No." value={shared.agreementNo} onChange={set("agreementNo")} />
          <Ctl label="Replacement Note No." value={shared.replacementNoteNo} onChange={set("replacementNoteNo")} />
          <Ctl label="Date" value={shared.date} onChange={set("date")} type="date" />
          <Ctl label="Coupon No." value={shared.couponNo} onChange={set("couponNo")} />
          <Ctl label="Payee / Holder (override)" value={shared.payee} onChange={set("payee")} />
          <Ctl label="Maker (override)" value={shared.maker} onChange={set("maker")} />
          <Ctl label="Face / Principal ($)" value={shared.faceAmount} onChange={set("faceAmount")} type="number" />
          <Ctl label="Interest Rate (%)" value={shared.interestRate} onChange={set("interestRate")} />
          <Ctl label="Term (months)" value={shared.termMonths} onChange={set("termMonths")} />
          <Ctl label="First Payment Date" value={shared.firstPaymentDate} onChange={set("firstPaymentDate")} type="date" />
          <Ctl label="Maturity Date" value={shared.maturityDate} onChange={set("maturityDate")} type="date" />
          <Ctl label="Coupon Designation" value={shared.designation} onChange={set("designation")} options={Object.entries(DESIGNATIONS).map(([v, l]) => ({ v, l }))} />
          <Ctl label="Arbitration Provider" value={shared.arbitrationProvider} onChange={set("arbitrationProvider")} />
          <Ctl label="Governing State" value={shared.governingState} onChange={set("governingState")} />
        </div>
        <div className="text-[10px] text-[#8A8A8A] mt-2" style={{ maxWidth: "8.5in", margin: "8px auto 0" }}>Borrower, lender, balance and property auto-populate from each borrower's file; blanks print as fillable lines. The Detachable Substitute Payment Coupon is a private, non-negotiable contractual instrument (Article XVIII / Schedule G).</div>
      </div>

      <div className="cm-print-area" style={{ padding: "24px 0" }}>
        {!apps.length
          ? <div style={{ color: "#9A9A90", textAlign: "center", padding: 40 }}>No application loaded. Open this from a borrower file, or pass ?ids=… for bulk.</div>
          : apps.map((a, i) => <MortgageReplacementDoc key={a.id} app={a} shared={shared} last={i === apps.length - 1} formKey={activeForm} />)}
      </div>
      <style>{MR_CSS}</style>
    </div>
  );
}

const btn = (color, border) => ({ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color, border: `1px solid ${border}`, padding: "9px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer" });
